<template>
  <div class="edit-plan-page">
    <!-- Background elements -->
    <div class="bg-grid"></div>
    <div class="bg-glow bg-glow-1"></div>

    <div class="edit-plan-container">
      <div class="page-header">
        <h1>Edit Subscription Plan</h1>
        <p class="subtitle">Update the plan details below</p>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner-large"></div>
        <p>Loading plan details...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
        <p>{{ error }}</p>
      </div>

      <div v-else-if="plan" class="edit-form-wrapper">
        <form @submit.prevent="handleUpdatePlan" class="edit-form">
          <!-- Plan Name -->
          <div class="form-group">
            <label for="name">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"/>
                <line x1="16" y1="8" x2="2" y2="22"/>
                <line x1="17.5" y1="15" x2="9" y2="15"/>
              </svg>
              Plan Name
            </label>
            <input
              type="text"
              id="name"
              v-model="formData.name"
              required
              placeholder="Enter plan name"
              class="form-input"
            />
          </div>

          <!-- Active Status -->
          <div class="form-group checkbox-group">
            <label class="checkbox-label">
              <div class="checkbox-wrapper">
                <input
                  type="checkbox"
                  id="isActive"
                  v-model="formData.isActive"
                  class="form-checkbox"
                />
                <div class="checkbox-custom">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
              </div>
              <div class="checkbox-text">
                <span class="checkbox-title">Active Plan</span>
                <span class="checkbox-description">Enable this plan for users to subscribe</span>
              </div>
            </label>
          </div>

          <!-- Inodes -->
          <div class="form-group">
            <label for="inodes">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
              </svg>
              Inodes Limit
            </label>
            <input
              type="number"
              id="inodes"
              v-model.number="formData.inodes"
              min="0"
              placeholder="Leave empty for unlimited"
              class="form-input"
            />
            <span class="form-hint">Maximum number of files that can be scanned</span>
          </div>

          <!-- Site Count -->
          <div class="form-group">
            <label for="siteCount">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
              Site Count
            </label>
            <input
              type="number"
              id="siteCount"
              v-model.number="formData.siteCount"
              required
              min="0"
              placeholder="Number of sites allowed"
              class="form-input"
            />
            <span class="form-hint">Number of domains the user can monitor</span>
          </div>

          <!-- Base Price -->
          <div class="form-group">
            <label for="basePrice">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="12" y1="1" x2="12" y2="23"/>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
              Base Price
            </label>
            <div class="input-with-addon">
              <span class="input-addon">$</span>
              <input
                type="number"
                id="basePrice"
                v-model.number="formData.basePrice"
                required
                min="0"
                step="0.01"
                placeholder="0.00"
                class="form-input with-addon"
              />
            </div>
          </div>

          <!-- Billing Cycle -->
          <div class="form-group">
            <label for="billingCycle">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              Billing Cycle
            </label>
            <select
              id="billingCycle"
              v-model="formData.billingCycle"
              required
              class="form-select"
            >
              <option value="">Select billing cycle</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          <!-- Error Message -->
          <Transition name="fade">
            <div v-if="updateError" class="error-message">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              <span>{{ updateError }}</span>
            </div>
          </Transition>

          <!-- Buttons -->
          <div class="button-group">
            <button type="button" @click="goBack" class="btn btn-secondary">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="19" y1="12" x2="5" y2="12"/>
                <polyline points="12 19 5 12 12 5"/>
              </svg>
              Cancel
            </button>
            <button type="submit" :disabled="updating" class="btn btn-primary">
              <span v-if="updating" class="spinner"></span>
              <template v-else>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Update Plan
              </template>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { api } from '../../utils/api'
import { useRouter, useRoute } from 'vue-router'

export default {
  name: 'EditSubscriptionPlanView',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const plan = ref(null)
    const loading = ref(true)
    const error = ref('')
    const updating = ref(false)
    const updateError = ref('')
    const formData = ref({
      name: '',
      isActive: false,
      inodes: null,
      siteCount: 1,
      basePrice: 0,
      billingCycle: ''
    })

    const fetchPlan = async () => {
      try {
        loading.value = true
        error.value = ''
        const planId = route.params.id
        const data = await api(`/admin/subscription-plans/${planId}`, {}, router)
        plan.value = data
        
        formData.value = {
          name: data.name || '',
          isActive: data.isActive !== undefined ? data.isActive : false,
          inodes: data.inodes !== null && data.inodes !== undefined ? data.inodes : null,
          siteCount: data.siteCount || 1,
          basePrice: data.basePrice || 0,
          billingCycle: data.billingCycle || ''
        }
      } catch (err) {
        console.error('Failed to fetch plan:', err)
        error.value = err.code || 'Something went wrong please contact support'
      } finally {
        loading.value = false
      }
    }

    const handleUpdatePlan = async () => {
      updating.value = true
      updateError.value = ''

      try {
        const planId = route.params.id
        await api(`/admin/subscription-plans/${planId}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: formData.value.name,
            isActive: formData.value.isActive,
            inodes: formData.value.inodes,
            siteCount: formData.value.siteCount,
            basePrice: formData.value.basePrice,
            billingCycle: formData.value.billingCycle
          })
        }, router)

        router.push('/admin/subscription-plans')
      } catch (err) {
        console.error('Failed to update plan:', err)
        updateError.value = err.code || 'Something went wrong please contact support'
      } finally {
        updating.value = false
      }
    }

    const goBack = () => {
      router.push('/admin/subscription-plans')
    }

    onMounted(() => {
      fetchPlan()
    })

    return {
      plan,
      loading,
      error,
      updating,
      updateError,
      formData,
      handleUpdatePlan,
      goBack
    }
  }
}
</script>

<style scoped>
.edit-plan-page {
  min-height: calc(100vh - 40px);
  position: relative;
  overflow: hidden;
  padding: 40px 20px;
}

/* Background elements */
.bg-grid {
  position: fixed;
  inset: 0;
  background-image: 
    linear-gradient(rgba(60, 98, 230, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(60, 98, 230, 0.02) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.bg-glow {
  position: fixed;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.3;
  pointer-events: none;
  z-index: 0;
}

.bg-glow-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(60, 98, 230, 0.25) 0%, transparent 70%);
  top: -100px;
  right: 10%;
  animation: float 20s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-30px, 30px); }
}

.edit-plan-container {
  position: relative;
  z-index: 1;
  max-width: 560px;
  margin: 0 auto;
  animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.page-header {
  margin-bottom: 32px;
}

.page-header h1 {
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.subtitle {
  color: var(--text-secondary);
  margin: 0;
  font-size: 0.95rem;
}

/* Loading & Error states */
.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: var(--text-secondary);
}

.loading-state p,
.error-state p {
  margin-top: 16px;
  font-size: 1rem;
}

.error-state svg {
  width: 48px;
  height: 48px;
  color: var(--error-color);
}

.error-state p {
  color: var(--error-color);
}

.spinner-large {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(60, 98, 230, 0.2);
  border-top-color: var(--accent-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Form */
.edit-form-wrapper {
  background: rgba(24, 24, 25, 0.7);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 
    0 20px 40px -12px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  overflow: hidden;
}

.edit-form {
  padding: 32px;
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-weight: 500;
  font-size: 0.9rem;
  color: var(--text-secondary);
}

.form-group label svg {
  width: 16px;
  height: 16px;
}

.form-hint {
  display: block;
  margin-top: 6px;
  font-size: 0.8rem;
  color: var(--text-muted);
}

.form-input,
.form-select {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-size: 1rem;
  font-family: inherit;
  background-color: rgba(61, 61, 61, 0.3);
  color: var(--text-primary);
  transition: all 0.2s ease;
}

.form-input::placeholder {
  color: var(--text-muted);
}

.form-input:hover,
.form-select:hover {
  border-color: rgba(255, 255, 255, 0.15);
  background-color: rgba(61, 61, 61, 0.4);
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: var(--accent-color);
  background-color: rgba(61, 61, 61, 0.5);
  box-shadow: 0 0 0 3px rgba(60, 98, 230, 0.15);
}

.form-select option {
  background-color: var(--bg-secondary);
  color: var(--text-primary);
}

/* Input with addon */
.input-with-addon {
  position: relative;
  display: flex;
  align-items: center;
}

.input-addon {
  position: absolute;
  left: 16px;
  color: var(--text-muted);
  font-size: 1rem;
  font-weight: 500;
  pointer-events: none;
}

.form-input.with-addon {
  padding-left: 36px;
}

/* Checkbox */
.checkbox-group {
  margin-bottom: 28px;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  cursor: pointer;
  padding: 16px;
  background: rgba(61, 61, 61, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  transition: all 0.2s ease;
}

.checkbox-label:hover {
  background: rgba(61, 61, 61, 0.3);
  border-color: rgba(255, 255, 255, 0.12);
}

.checkbox-wrapper {
  position: relative;
  flex-shrink: 0;
}

.form-checkbox {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.checkbox-custom {
  width: 22px;
  height: 22px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  background: rgba(61, 61, 61, 0.3);
}

.checkbox-custom svg {
  width: 14px;
  height: 14px;
  opacity: 0;
  transform: scale(0.5);
  transition: all 0.2s ease;
  color: white;
}

.form-checkbox:checked + .checkbox-custom {
  background: linear-gradient(135deg, var(--accent-color) 0%, #2a4bc4 100%);
  border-color: var(--accent-color);
}

.form-checkbox:checked + .checkbox-custom svg {
  opacity: 1;
  transform: scale(1);
}

.checkbox-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.checkbox-title {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.95rem;
}

.checkbox-description {
  font-size: 0.85rem;
  color: var(--text-muted);
}

/* Error message */
.error-message {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: rgba(221, 82, 76, 0.1);
  border: 1px solid rgba(221, 82, 76, 0.2);
  border-radius: 12px;
  color: var(--error-color);
  margin-bottom: 24px;
  font-size: 0.9rem;
}

.error-message svg {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 2px;
}

/* Buttons */
.button-group {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  font-family: inherit;
  transition: all 0.2s ease;
}

.btn svg {
  width: 18px;
  height: 18px;
}

.btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-primary {
  background: linear-gradient(135deg, var(--accent-color) 0%, #2a4bc4 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(60, 98, 230, 0.3);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(60, 98, 230, 0.4);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(0);
}

.btn-secondary {
  background-color: rgba(255, 255, 255, 0.05);
  color: var(--text-primary);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover {
  background-color: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
}

/* Spinner */
.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Responsive */
@media (max-width: 640px) {
  .edit-plan-page {
    padding: 20px 16px;
  }

  .edit-form {
    padding: 24px 20px;
  }

  .button-group {
    flex-direction: column-reverse;
  }

  .btn {
    width: 100%;
  }

  .checkbox-label {
    padding: 14px;
  }
}
</style>
